import {redirect, type LoaderArgs, defer} from '@shopify/remix-oxygen';
import {CACHE_LONG, routeHeaders} from '~/data/cache';
import {canonicalUrl} from '~/lib/seo.server';
import {sanityImgBuilder} from 'domains/sanity';
import {legacyRedirectTarget} from '~/lib/legacyRedirects';
import type {LinksFunction} from '@shopify/remix-oxygen';

//domains
import {
  getLangagueCodeForSanityQuery,
  getCountryCodeForSanityQuery,
} from '~/domains/sanity';
import {useBlogDetailsQuery as fetchBlogDetails} from 'domains/blog';
import {truncate} from 'domains/global';

//components
import BlogDetails from '~/components/blogDetails';

//translation
import translation from 'translations/blog.json';
import {routeLocale} from 'domains/global';

export const headers = routeHeaders;

export const links: LinksFunction = () => {
  return [
    {
      rel: 'stylesheet',
      href: 'https://cdn.jsdelivr.net/npm/swiper@8/swiper-bundle.min.css',
    },
  ];
};

export async function loader({request, params, context}: LoaderArgs) {
  if (!params.slug) {
    throw new Response(null, {status: 404});
  }

  const {blog, blogSettings, recentBlogs, tags, categories} =
    await fetchBlogDetails({
      languageCode: getLangagueCodeForSanityQuery(
        context.storefront.i18n.language,
      ),
      countryCode: getCountryCodeForSanityQuery(
        context.storefront.i18n.country,
      ),
      slug: params.slug,
    });

  // A slug Sanity does not know used to fall through to
  // blog.currentBlog.blog_title below and 500 on every dead blog URL —
  // Search Console's 5xx bucket was largely this. Renamed posts live in the
  // ported Shopify redirect table; everything else is an honest 404.
  if (!blog?.currentBlog) {
    const legacy = legacyRedirectTarget(new URL(request.url).pathname);
    if (legacy) {
      throw redirect(legacy, 301);
    }
    throw new Response(null, {status: 404});
  }

  // The post is looked up by slug alone, so /blogs/<anything>/<slug> rendered
  // it — every post was reachable at unlimited URLs, each self-canonical.
  // Sending the wrong category to the real one collapses those duplicates and
  // happens to recover the store's old /blogs/news/<slug> links for free.
  const categorySlug = blog?.currentBlog?.blog_category?.slug?.current;
  if (categorySlug && categorySlug !== params.categoryHandle) {
    const {search} = new URL(request.url);
    const langPrefix = params.lang ? `/${params.lang}` : '';
    throw redirect(
      `${langPrefix}/blogs/${categorySlug}/${params.slug}${search}`,
      301,
    );
  }

  // A post used to describe itself as @type Blog — the container, not the
  // article — so no article page ever qualified for Article rich results
  // and AI engines had no headline, date or author to cite. BlogPosting is
  // what a single post is; the breadcrumb gives it a place in the site.
  const sourcePost = blog.currentBlog;
  const isEnglish = context.storefront.i18n.language === 'EN';
  // Translated titles alone do not establish an English article: most posts
  // have an English title but fall back to the Chinese body. Supply the same
  // availability signal on both locale routes for reciprocal hreflang output.
  const hasEnglishTranslation = Boolean(
    sourcePost.page_sections_en?.length && sourcePost.blog_title_en?.trim(),
  );
  const hasTraditionalTranslation = Boolean(
    sourcePost.page_sections_zh_tw?.length &&
      sourcePost.blog_title_zh_tw?.trim(),
  );
  const usesChineseFallback = isEnglish && !sourcePost.page_sections_en?.length;
  const usesEnglishFallback =
    !isEnglish &&
    !sourcePost.page_sections_zh_tw?.length &&
    Boolean(sourcePost.page_sections_en?.length);
  const post = {
    ...sourcePost,
    // An empty module array is also missing content; normalize it so the
    // component's nullish fallback renders the available Chinese article.
    page_sections_en: sourcePost.page_sections_en?.length
      ? sourcePost.page_sections_en
      : undefined,
    page_sections_zh_tw: sourcePost.page_sections_zh_tw?.length
      ? sourcePost.page_sections_zh_tw
      : undefined,
    blog_title:
      (usesChineseFallback
        ? sourcePost.blog_title_zh_tw
        : usesEnglishFallback
        ? sourcePost.blog_title_en
        : sourcePost.blog_title
      )?.trim() ||
      sourcePost.blog_title_zh_tw ||
      sourcePost.blog_title_en ||
      '',
    blog_description:
      (usesChineseFallback
        ? sourcePost.blog_description_zh_tw
        : usesEnglishFallback
        ? sourcePost.blog_description_en
        : sourcePost.blog_description
      )?.trim() ||
      sourcePost.blog_description_zh_tw ||
      sourcePost.blog_description_en ||
      '',
  };
  const pageUrl = canonicalUrl(request.url);
  const origin = new URL(pageUrl).origin;
  const thumbnailRef = post.blog_thumbnail?.asset?._ref;
  const thumbnail = thumbnailRef
    ? sanityImgBuilder
        .image({_ref: thumbnailRef})
        .width(1200)
        .auto('format')
        .url()
    : undefined;
  const categoryTitle = post.blog_category?.category_title;
  const categoryPath = post.blog_category?.slug?.current
    ? `${origin}${params.lang ? `/${params.lang}` : ''}/blogs/${
        post.blog_category.slug.current
      }`
    : undefined;
  const seo = {
    title: post.blog_title,
    description: truncate(post.blog_description),
    url: pageUrl,
    ...(thumbnail
      ? {
          media: {
            type: 'image' as const,
            url: thumbnail,
            altText: post.blog_title,
          },
        }
      : {}),
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        '@id': `${pageUrl}#article`,
        mainEntityOfPage: pageUrl,
        url: pageUrl,
        headline: post.blog_title,
        description: truncate(post.blog_description),
        ...(thumbnail ? {image: thumbnail} : {}),
        ...(post.blog_published_date
          ? {datePublished: post.blog_published_date}
          : {}),
        ...(post._updatedAt ? {dateModified: post._updatedAt} : {}),
        // Most posts have no English body, and /en then shows the Chinese
        // one (BlogDetails falls back), so only claim 'en' when it exists.
        inLanguage:
          (isEnglish && post.page_sections_en?.length) || usesEnglishFallback
            ? 'en'
            : 'zh-Hant',
        ...(categoryTitle ? {articleSection: categoryTitle} : {}),
        author: {
          '@type': 'Organization',
          name: 'Helmetking 頭盔王',
          url: origin,
        },
        publisher: {'@id': `${origin}/#organization`},
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {'@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/`},
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Blog',
            item: `${origin}${params.lang ? `/${params.lang}` : ''}/blogs`,
          },
          ...(categoryTitle && categoryPath
            ? [
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: categoryTitle,
                  item: categoryPath,
                },
              ]
            : []),
          {
            '@type': 'ListItem',
            position: categoryTitle && categoryPath ? 4 : 3,
            name: post.blog_title,
          },
        ],
      },
    ],
  };

  const selectedLocale =
    context.storefront.i18n.language.toLowerCase() as routeLocale;
  return defer(
    {
      seo,
      hasEnglishTranslation,
      hasTraditionalTranslation,
      translation: translation[selectedLocale],
      currentBlog: post,
      nextBlog: blog.nextBlog,
      previousBlog: blog.previousBlog,
      blogSettings,
      recentBlogs,
      tags,
      categories,
    },
    {
      headers: {
        'Cache-Control': CACHE_LONG,
      },
    },
  );
}

export default function Page() {
  return <BlogDetails />;
}
